
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Info } from "lucide-react";
import { AdapterMountDialog } from "@/components/inventory/AdapterMountDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AdapterSectionProps {
  hasAdapter: boolean;
  toggleAdapterCompatibility: () => Promise<void>;
  adapterMounts: string[];
  handleAddAdapterMount: (mountName: string) => Promise<void>;
  handleRemoveAdapterMount: (mountName: string) => Promise<void>;
  formatMount: (mount: string) => string;
}

export function AdapterSection({
  hasAdapter,
  toggleAdapterCompatibility,
  adapterMounts,
  handleAddAdapterMount,
  handleRemoveAdapterMount,
  formatMount
}: AdapterSectionProps) {
  return (
    <div className="pt-4 border-t mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label htmlFor="adapter-compatibility" className="font-medium">
            Lens Adapter Compatibility
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">Enable this if you have lens adapters that allow the camera to use lenses with different mounts.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Switch
          id="adapter-compatibility"
          checked={hasAdapter}
          onCheckedChange={toggleAdapterCompatibility}
        />
      </div>
      
      {hasAdapter && (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Adapter Mounts</p>
            <AdapterMountDialog onAddMount={handleAddAdapterMount}>
              <Button size="sm" variant="outline">
                Add Mount
              </Button>
            </AdapterMountDialog>
          </div>
          
          {adapterMounts.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No adapter mounts added yet</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {adapterMounts.map((mount, index) => (
                <Badge key={index} variant="outline" className="gap-1">
                  {formatMount(mount)}
                  <button 
                    onClick={() => handleRemoveAdapterMount(mount)}
                    className="ml-1 text-muted-foreground hover:text-destructive"
                  >
                    &times;
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
